import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import styled, { useTheme } from 'styled-components';
import { BodySmall } from '../Texts';
import {
  TextInputContainer,
  StyledInput as StyledTextInput,
} from '../Inputs/Text';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';
import { useGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { useSolana } from '../../hooks/useSolana';
import { useDispatch, useSelector } from 'react-redux';
import { selectTokenBalance } from '../../redux/slices/walletSlice';
import {
  selectTokenBySymbol,
  selectTokensWithUSDBalance,
  selectAllTokensByNetwork,
} from '../../redux/slices/tokenSlice';
import { openModal } from '../../redux/slices/modals';
import debounce from 'lodash/debounce';
import { formatTokenPrice } from '../../common/currency';
import { usePrivy } from '@privy-io/react-auth';
import { EVENT_SWAP_INPUT_CHANGED, sendEvent } from '../../common/analytics';
import { hapticsImpactLight } from '../../common/haptic';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import SwapIcon from '../../assets/icons/swap-arrows.svg?react';

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StyledInput = styled(StyledTextInput)`
  border: none;
  width: 100%;
  font-size: 24px;
  font-weight: 500;
  text-align: left;
  border-radius: 0;
  background-color: ${({ theme }) => theme.background.secondary};
  color: ${({ theme }) => theme.text.regular};

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }

  /* Remove spinner buttons for webkit browsers (Chrome, Safari, etc.) */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove spinner buttons for Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  /* Ensure no browser default padding */
  &:not(:placeholder-shown) {
    padding-right: 0;
  }

  &:focus {
    outline: none;
  }
`;

const TokenInput = styled(TextInputContainer)<{
  $isFocused?: boolean;
  $hasError?: boolean;
}>`
  display: flex;
  align-items: center;
  border-radius: 16px;
  padding: 12px;
  height: auto;
  color: ${({ theme }) => theme.text.regular};
  background-color: ${({ theme }) => theme.background.secondary};
`;

export const Label = styled(BodySmall)`
  color: ${({ theme }) => theme.text.secondary};
  margin-bottom: 0px;
  font-size: 12px;
`;

const ErrorMessage = styled(BodySmall)`
  color: ${({ theme }) => theme.text.error};
`;

const MaxButton = styled.button`
  background: none;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.text.title};
  padding: 0px 6px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;
  z-index: 1;
  font-size: 12px;
  ${({ disabled, theme }) =>
    disabled &&
    `
    cursor: auto;
    border: 1px solid ${theme.text.secondary};
    color: ${theme.text.secondary};
  `}
`;

interface TokenSelectOption {
  value: string;
  label: string;
  name: string;
  logoURI: string;
  network?: string;
}

interface TokenSwapInputsProps {
  fromAmount: string;
  onFromAmountChange: (value: string) => void;
  onFromTokenChange?: (token: TokenSelectOption) => void;
  onToTokenChange?: (token: TokenSelectOption) => void;
  fromTokenSelectable?: boolean;
  toTokenSelectable?: boolean;
  defaultFromToken?: string; // token address
  defaultToToken?: string; // token address
  fixedTokenAddress?: string; // token address that cannot be changed (but can switch from/to)
  disabled?: boolean;
  showMaxButton?: boolean;
  showFromBalance?: boolean;
  showToBalance?: boolean;
  slippageBps?: string;
  feeBps?: string;
  onQuoteChange?: (quote: any) => void;
  onToAmountChange?: (value: string) => void;
  error?: string | null;
  isGift?: boolean;
}

const BalanceText = styled.span`
  color: ${({ theme }) => theme.text.light};
  white-space: nowrap;
  display: inline-block;
  text-align: right;
`;

const BalanceContainer = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};
  height: ${(props) => (props.$visible ? 'auto' : '20px')};
  transition:
    opacity 0.2s ease,
    height 0.2s ease;
`;

const InfoContainer = styled.div<{ $visible: boolean }>`
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  height: ${(props) => (props.$visible ? 'auto' : '20px')};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

const InputsContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChangeDirectionContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const ChangeDirection = styled.div`
  cursor: pointer;
  pointer-events: all;
  height: 32px;
  width: 32px;
  border-radius: 8px;
  outline: thick solid ${({ theme }) => theme.background.primary};
  background-color: ${({ theme }) => theme.primary.ultraLight};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TokenSelectorButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.background.secondPrimary};
  cursor: pointer;
  width: fit-content;

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => theme.background.light};
    }
  }

  img {
    color: ${({ theme }) => theme.text.regular};
  }

  ${({ disabled }) =>
    disabled &&
    `
    cursor: auto;
    opacity: 0.7;
    pointer-events: none;
  `}
`;

const FixedTokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  min-width: 105px;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  min-width: 0;
  width: fit-content;
  gap: 6px;
`;

const TokenSymbol = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TokenSwapInputs: React.FC<TokenSwapInputsProps> = ({
  fromAmount,
  onFromAmountChange,
  onFromTokenChange,
  onToTokenChange,
  defaultFromToken,
  defaultToToken,
  fixedTokenAddress,
  disabled = false,
  showMaxButton = true,
  showFromBalance = true,
  showToBalance = true,
  slippageBps = 'auto',
  onQuoteChange,
  onToAmountChange,
  error: formError,
  isGift = false,
}) => {
  const theme = useTheme();
  const { fromTokenAmount, toTokenAmount, refreshTokenBalances } = useSolana();
  const [fromInputFocused, setFromInputFocused] = useState(false);
  const [toInputFocused, setToInputFocused] = useState(false);
  const [toAmount, setToAmount] = useState('');
  const [internalError, setInternalError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Get token lists from Redux
  const tokensWithBalance = useSelector((state) =>
    selectTokensWithUSDBalance(state),
  );
  const allTokens = useSelector((state) =>
    selectAllTokensByNetwork(state, 'solana'),
  );
  const [debouncedFromAmount, setDebouncedFromAmount] = useState(fromAmount);
  const [selectedFromTokenAddress, setSelectedFromTokenAddress] = useState(
    defaultFromToken || null,
  );
  const [selectedToTokenAddress, setSelectedToTokenAddress] = useState(
    defaultToToken || null,
  );
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);

  const { user } = usePrivy();

  // TODO: Refactor this to the TokenAmountInput component
  useEffect(() => {
    const fromInput = fromInputRef.current;
    const toInput = toInputRef.current;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
    };

    if (fromInput) {
      fromInput.addEventListener('wheel', handleWheel, { passive: false });
    }
    if (toInput) {
      toInput.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (fromInput) {
        fromInput.removeEventListener('wheel', handleWheel);
      }
      if (toInput) {
        toInput.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  // Convert tokens to select options format for compatibility
  const createTokenSelectOption = (token: any): TokenSelectOption => ({
    value: token.address,
    label: token.symbol,
    name: token.name,
    logoURI: token.logoURI,
    network: token.network || 'solana',
    decimals: token.decimals || 6,
  });

  // Get selected tokens from Redux arrays using addresses
  // This way we have updated price / balance information
  const selectedFromToken = allTokens.find(
    (token) => token.address === selectedFromTokenAddress,
  );
  const selectedToToken = allTokens.find(
    (token) => token.address === selectedToTokenAddress,
  );

  // Get token objects for balance lookups
  const fromTokenObj = useSelector((state) =>
    selectedFromToken
      ? selectTokenBySymbol(
          state,
          selectedFromToken.network || 'solana',
          selectedFromToken.symbol,
        )
      : null,
  );
  const toTokenObj = useSelector((state) =>
    selectedToToken
      ? selectTokenBySymbol(
          state,
          selectedToToken.network || 'solana',
          selectedToToken.symbol,
        )
      : null,
  );

  // Get balances using selectors
  const fromTokenBalance = useSelector((state: any) =>
    selectedFromToken
      ? selectTokenBalance(
          selectedFromToken.network || 'solana',
          fromTokenObj?.address || '',
        )(state)
      : 0,
  );
  const toTokenBalance = useSelector((state: any) =>
    selectedToToken
      ? selectTokenBalance(
          selectedToToken.network || 'solana',
          toTokenObj?.address || '',
        )(state)
      : 0,
  );

  const handleFromTokenChange = (token: any) => {
    const tokenOption = createTokenSelectOption(token);
    setSelectedFromTokenAddress(token.address);
    onFromTokenChange?.(tokenOption);
  };

  const handleToTokenChange = (token: any) => {
    const tokenOption = createTokenSelectOption(token);
    setSelectedToTokenAddress(token.address);
    onToTokenChange?.(tokenOption);
  };

  // Determine if token selectors should be disabled based on fixed token
  const isFromTokenFixed =
    fixedTokenAddress && selectedFromTokenAddress === fixedTokenAddress;
  const isToTokenFixed =
    fixedTokenAddress && selectedToTokenAddress === fixedTokenAddress;

  const handleDirectionChange = () => {
    // Swap the selected tokens
    const tempFromAddress = selectedFromTokenAddress;
    const tempToAddress = selectedToTokenAddress;

    setSelectedFromTokenAddress(tempToAddress);
    setSelectedToTokenAddress(tempFromAddress);

    // Clear the from amount when swapping direction
    onFromAmountChange('');

    // Notify parent components about the token changes
    if (selectedToToken && onFromTokenChange) {
      const tokenOption = createTokenSelectOption(selectedToToken);
      onFromTokenChange(tokenOption);
    }
    if (selectedFromToken && onToTokenChange) {
      const tokenOption = createTokenSelectOption(selectedFromToken);
      onToTokenChange(tokenOption);
    }

    hapticsImpactLight();
  };
  // Create debounced setter
  const debouncedSetAmount = useCallback(
    debounce((value: string) => {
      sendEvent(EVENT_SWAP_INPUT_CHANGED);
      setDebouncedFromAmount(value);
    }, 500) as ((value: string) => void) & { cancel: () => void },
    [],
  );

  // Update debounced value when fromAmount changes
  useEffect(() => {
    debouncedSetAmount(fromAmount);
    return () => {
      debouncedSetAmount.cancel();
    };
  }, [fromAmount, debouncedSetAmount]);

  const validateAmount = (value: string) => {
    if (!value) {
      setInternalError(null);
      return true;
    }

    const amount = parseFloat(value);

    if (isNaN(amount) || amount < 0) {
      setInternalError('Invalid amount');
      return false;
    }

    setInternalError(null);
    return true;
  };

  const calculateMaxAmount = () => {
    const balance = fromTokenBalance.toString();
    const decimals = selectedFromToken?.decimals || 6;

    const balanceNum = parseFloat(balance);

    // Treat dust amounts, invalid numbers, or zero as zero
    if (!isFinite(balanceNum) || balanceNum < 1e-10) {
      return {
        display: '0.00',
        max: '0',
      };
    }

    // Convert number to decimal string with enough precision
    const normalizedBalance = balanceNum.toFixed(Math.max(decimals, 15));

    // Split the balance into whole and decimal parts
    const [whole, fraction = ''] = normalizedBalance.split('.');

    // Pad the fraction with zeros to match decimals
    const paddedFraction = fraction.padEnd(decimals, '0').slice(0, decimals);

    // Combine whole and fraction parts
    const rawAmount = BigInt(whole + paddedFraction);

    // If it ends in 1, subtract 1 to avoid Raydium's precision issue
    const adjustedRawAmount = rawAmount.toString().endsWith('1')
      ? rawAmount - 1n
      : rawAmount;

    // Convert back to decimal string
    const maxAmountStr = adjustedRawAmount.toString();
    const decimalPos = maxAmountStr.length - decimals;
    const maxAmount =
      decimalPos > 0
        ? maxAmountStr.slice(0, decimalPos) +
          '.' +
          maxAmountStr.slice(decimalPos)
        : '0.' + '0'.repeat(-decimalPos) + maxAmountStr;

    return {
      display: formatTokenPrice(maxAmount),
      max: maxAmount,
    };
  };

  const handleMaxClick = () => {
    const { max } = calculateMaxAmount();
    if (max && parseFloat(max) > 0) {
      handleFromAmountChange(max);
    }
  };

  const { data: quoteData } = useGetSwapQuoteQuery(
    {
      inputMint: selectedFromToken?.address,
      outputMint: selectedToToken?.address,
      amount: debouncedFromAmount
        ? toTokenAmount(debouncedFromAmount, selectedFromToken?.decimals || 6)
        : '0',
      slippageBps,
      calculateFees: true,
      isGift,
    },
    {
      skip:
        !selectedFromToken?.address ||
        !selectedToToken?.address ||
        !debouncedFromAmount,
      refetchOnMountOrArgChange: true,
      pollingInterval: 5000,
    },
  );

  // Get USDC quotes for both tokens to show USD values
  const { data: fromTokenToUSDCQuote } = useGetSwapQuoteQuery(
    {
      inputMint: selectedFromToken?.address,
      outputMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC mint address
      amount: debouncedFromAmount
        ? toTokenAmount(debouncedFromAmount, selectedFromToken?.decimals || 6)
        : '0',
      slippageBps: 'auto',
      calculateFees: false,
      isGift: false,
    },
    {
      skip: !selectedFromToken?.address || !debouncedFromAmount,
      refetchOnMountOrArgChange: true,
      pollingInterval: 10000, // Less frequent since it's just for display
    },
  );

  const { data: toTokenToUSDCQuote } = useGetSwapQuoteQuery(
    {
      inputMint: selectedToToken?.address,
      outputMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC mint address
      amount: toAmount
        ? toTokenAmount(toAmount, selectedToToken?.decimals || 6)
        : '0',
      slippageBps: 'auto',
      calculateFees: false,
      isGift: false,
    },
    {
      skip: !selectedToToken?.address || !toAmount,
      refetchOnMountOrArgChange: true,
      pollingInterval: 10000, // Less frequent since it's just for display
    },
  );

  const processedQuote = useMemo(() => {
    if (!quoteData?.quote?.outAmount || !debouncedFromAmount) {
      return null;
    }

    return {
      outAmount: quoteData.quote.outAmount,
      fromAmount: debouncedFromAmount,
      decimals:
        selectedFromToken?.address === selectedToToken?.address
          ? 6
          : selectedToToken?.decimals || 9,
      fees: quoteData.fees,
      amounts: quoteData.amounts,
    };
  }, [
    quoteData,
    debouncedFromAmount,
    selectedToToken?.decimals,
    selectedFromToken?.address,
    selectedToToken?.address,
  ]);

  const formattedAmount = useMemo(() => {
    if (!processedQuote) {
      return '';
    }

    return fromTokenAmount(
      processedQuote.outAmount,
      processedQuote.decimals,
    ).toString();
  }, [processedQuote, fromTokenAmount]);

  // Calculate USD values for display
  const fromTokenUSDValue = useMemo(() => {
    if (!fromTokenToUSDCQuote?.quote?.outAmount || !debouncedFromAmount) {
      return null;
    }
    return fromTokenAmount(fromTokenToUSDCQuote.quote.outAmount, 6).toString(); // USDC has 6 decimals
  }, [fromTokenToUSDCQuote, debouncedFromAmount, fromTokenAmount]);

  const toTokenUSDValue = useMemo(() => {
    if (!toTokenToUSDCQuote?.quote?.outAmount || !toAmount) {
      return null;
    }
    return fromTokenAmount(toTokenToUSDCQuote.quote.outAmount, 6).toString(); // USDC has 6 decimals
  }, [toTokenToUSDCQuote, toAmount, fromTokenAmount]);

  useEffect(() => {
    if (!processedQuote) {
      if (toAmount !== '') {
        setToAmount('');
        onToAmountChange?.('');
        onQuoteChange?.(null);
      }
      return;
    }

    const newAmount = formattedAmount;
    if (toAmount !== newAmount) {
      setToAmount(newAmount);
      onToAmountChange?.(newAmount);
    }
    onQuoteChange?.(quoteData);
  }, [
    processedQuote,
    formattedAmount,
    quoteData,
    toAmount,
    onToAmountChange,
    onQuoteChange,
  ]);

  const handleFromAmountChange = (
    e: React.ChangeEvent<HTMLInputElement> | string,
  ) => {
    const value = typeof e === 'string' ? e : e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      validateAmount(value);
      onFromAmountChange(value);
    }
  };

  const handleToAmountChange = (
    e: React.ChangeEvent<HTMLInputElement> | string,
  ) => {
    const value = typeof e === 'string' ? e : e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setToAmount(value);
      onToAmountChange?.(value);
    }
  };

  useEffect(() => {
    if (user && !disabled) {
      refreshTokenBalances();
    }
  }, []);

  return (
    <>
      <InputsContainer>
        <ChangeDirectionContainer>
          <ChangeDirection onClick={handleDirectionChange}>
            <SwapIcon
              width={16}
              height={16}
              style={{ color: theme.text.regular }}
            />
          </ChangeDirection>
        </ChangeDirectionContainer>
        <InputContainer>
          <TokenInput
            $isFocused={fromInputFocused}
            $hasError={!!(formError || internalError)}
          >
            <InputWrapper>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'flex-end',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Label>You Pay</Label>
                  <StyledInput
                    inputMode='decimal'
                    type='number'
                    value={fromAmount || ''}
                    onChange={handleFromAmountChange}
                    placeholder='0.00'
                    disabled={disabled}
                    onFocus={() => setFromInputFocused(true)}
                    onBlur={() => setFromInputFocused(false)}
                    ref={fromInputRef}
                  />
                  <BalanceContainer $visible={true}>
                    <Label>
                      <BalanceText>
                        {fromTokenUSDValue
                          ? `≈ $${parseFloat(fromTokenUSDValue).toFixed(2)}`
                          : ''}
                      </BalanceText>
                    </Label>
                  </BalanceContainer>
                </div>
                <TokenInfo>
                  {isFromTokenFixed ? (
                    <FixedTokenDisplay>
                      {selectedFromToken ? (
                        <>
                          <TokenLogo src={selectedFromToken.logoURI} />
                          <TokenSymbol>{selectedFromToken.symbol}</TokenSymbol>
                        </>
                      ) : (
                        <TokenSymbol>Select Token</TokenSymbol>
                      )}
                    </FixedTokenDisplay>
                  ) : (
                    <TokenSelectorButton
                      disabled={disabled}
                      onClick={() =>
                        !disabled &&
                        dispatch(
                          openModal({
                            name: 'selectToken',
                            props: {
                              title: 'Select Token to Sell',
                              tokens: tokensWithBalance,
                              filterToken: selectedToToken, // Exclude the "to" token
                              onContinue: handleFromTokenChange,
                            },
                          }),
                        )
                      }
                    >
                      {selectedFromToken ? (
                        <>
                          <TokenLogo src={selectedFromToken.logoURI} />
                          <TokenSymbol>{selectedFromToken.symbol}</TokenSymbol>
                        </>
                      ) : (
                        <TokenSymbol>Select Token</TokenSymbol>
                      )}
                      <ChevronRightIcon width={12} height={12} />
                    </TokenSelectorButton>
                  )}
                  <BalanceContainer
                    $visible={showFromBalance && !!selectedFromToken}
                  >
                    <Label>
                      <BalanceText>{calculateMaxAmount().display} </BalanceText>
                    </Label>
                    {showMaxButton && (
                      <MaxButton
                        onClick={handleMaxClick}
                        disabled={
                          disabled || parseFloat(calculateMaxAmount().max) === 0
                        }
                      >
                        Max
                      </MaxButton>
                    )}
                  </BalanceContainer>
                </TokenInfo>
              </div>
            </InputWrapper>
          </TokenInput>
        </InputContainer>

        <InputContainer>
          <TokenInput $isFocused={toInputFocused}>
            <InputWrapper>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'flex-end',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Label>You Receive</Label>
                  <StyledInput
                    inputMode='decimal'
                    type='number'
                    value={toAmount || ''}
                    onChange={handleToAmountChange}
                    placeholder='0.00'
                    disabled={disabled || !onToAmountChange || !onToTokenChange}
                    onFocus={() => setToInputFocused(true)}
                    onBlur={() => setToInputFocused(false)}
                    ref={toInputRef}
                  />
                  <BalanceContainer $visible={true}>
                    <Label>
                      <BalanceText>
                        {toTokenUSDValue
                          ? `≈ $${parseFloat(toTokenUSDValue).toFixed(2)}`
                          : ''}
                      </BalanceText>
                    </Label>
                  </BalanceContainer>
                </div>
                <TokenInfo>
                  {isToTokenFixed ? (
                    <FixedTokenDisplay>
                      {selectedToToken ? (
                        <>
                          <TokenLogo src={selectedToToken.logoURI} />
                          <TokenSymbol>{selectedToToken.symbol}</TokenSymbol>
                        </>
                      ) : (
                        <TokenSymbol>Select Token</TokenSymbol>
                      )}
                    </FixedTokenDisplay>
                  ) : (
                    <TokenSelectorButton
                      disabled={disabled}
                      onClick={() =>
                        !disabled &&
                        dispatch(
                          openModal({
                            name: 'selectToken',
                            props: {
                              title: 'Select Token to Buy',
                              filterToken: selectedFromToken, // Exclude the "from" token
                              onContinue: handleToTokenChange,
                            },
                          }),
                        )
                      }
                    >
                      {selectedToToken ? (
                        <>
                          <TokenLogo src={selectedToToken.logoURI} />
                          <TokenSymbol>{selectedToToken.symbol}</TokenSymbol>
                        </>
                      ) : (
                        <TokenSymbol>Select Token</TokenSymbol>
                      )}
                      <ChevronRightIcon width={12} height={12} />
                    </TokenSelectorButton>
                  )}
                  <BalanceContainer
                    $visible={showToBalance && !!selectedToToken}
                  >
                    <Label>
                      <BalanceText>
                        {formatTokenPrice(toTokenBalance)}{' '}
                      </BalanceText>
                    </Label>
                  </BalanceContainer>
                </TokenInfo>
              </div>
            </InputWrapper>
          </TokenInput>
        </InputContainer>
      </InputsContainer>
      {!!(
        selectedFromToken?.address === selectedToToken?.address &&
        processedQuote
      ) ||
        (!!(formError || internalError) && (
          <div style={{ position: 'relative' }}>
            <InfoContainer
              $visible={
                !!(
                  selectedFromToken?.address === selectedToToken?.address &&
                  processedQuote
                ) || !!(formError || internalError)
              }
            >
              {selectedFromToken?.address === selectedToToken?.address &&
              processedQuote &&
              !formError &&
              !internalError ? (
                <Label>≈ ${parseFloat(formattedAmount).toFixed(2)} USDC</Label>
              ) : (
                <>
                  <img
                    src={'/errorIcon.svg'}
                    alt='error'
                    style={{ width: '16px', height: '16px' }}
                  />
                  <ErrorMessage>{formError || internalError}</ErrorMessage>
                </>
              )}
            </InfoContainer>
          </div>
        ))}
    </>
  );
};

export default TokenSwapInputs;
